<template>
  <header class="header-live">
    <div class="header-wrapper-shadow">
      <div class="header-container container">
        <a :href="routes['site.page_home.index']" class="header__logo">
          <img src="@/assets/images/logo-new.svg" class="header__logo__pc img" alt="logo" width="160" height="58" />
          <img src="@/assets/images/logo-mob.svg" class="header__logo__mob img" alt="logo" width="43" height="58" />
        </a>
        <div class="auction-live-info">
          <a :href="routes['site.auction.show']" class="auction-title">{{ auction.title || "" }}</a>
          <div v-show="auction?.id" class="live-progress">
            <span class="progress">
              <span class="progress-line" :style="{ width: `${auctionProgress}%` }"></span>
            </span>
            <div class="progress-label">
              <template v-if="auctionStore.user?.is_admin">
                {{ passedLots.length }} / {{ lots.length }} [{{ auctionProgress }}%]
              </template>
              <template v-else>{{ auctionProgress }}%</template>
            </div>
          </div>
        </div>
        <div class="header__right">
          <div v-if="!auctionStore.user?.is_admin" class="count-bidders">
            <base-icon icon-name="watch" width="26" height="16" view-box="18.756 16.382 26.244 16.235">
              <watch-icon></watch-icon>
            </base-icon>
            {{ trans("header.live_bidders_count", { count: auctionStore?.userOnLiveAnonymous || 0 }) }}
          </div>

          <div class="header__menu__language-switcher">
            <v-menu placement="bottom" theme="info-tooltip" :distance="30">
              <span class="language-switcher__text">{{ auctionStore.languageSettings.language }}</span>
              <template #popper>
                <div class="languages-list">
                  <div v-for="language in languages" class="languages-list__inner-wrapper" :key="language.id">
                    <a class="languages-list__item" :href="language.url" @click.prevent.stop="switchLanguage(language)">
                      {{ language.abbr }}
                    </a>
                  </div>
                </div>
              </template>
            </v-menu>
          </div>

          <div v-if="auctionStore.user" class="header__menu__login profile-name">
            <div class="profile-name__text">
              <v-menu placement="bottom" theme="info-tooltip" :distance="30">
                <div class="user-name">{{ auctionStore.user?.first_name || "" }}</div>
                <template #popper>
                  <div class="profile-menu">
                    <a :href="routes['profile.bids.active']" class="profile-item__inner-wrapper">
                      <span class="profile-item">
                        {{ trans("header.user_menu.bids") }}
                      </span>
                    </a>
                    <a :href="routes['profile.invoices.index']" class="profile-item__inner-wrapper">
                      <span class="profile-item">
                        {{ trans("header.user_menu.purchases") }}
                      </span>
                    </a>
                    <a :href="routes['profile.info.edit']" class="profile-item__inner-wrapper">
                      <span class="profile-item">
                        {{ trans("header.user_menu.profile") }}
                      </span>
                    </a>
                    <a :href="routes['profile.notifications.edit']" class="profile-item__inner-wrapper">
                      <span class="profile-item">
                        {{ trans("header.user_menu.notifications") }}
                      </span>
                    </a>

                    <a href="#" @click.prevent="logout" class="profile-item__inner-wrapper-logout">
                      <span class="profile-item-logout">
                        {{ trans("header.user_menu.logout") }}
                      </span>
                    </a>
                  </div>
                </template>
              </v-menu>
            </div>
          </div>
          <a v-else :href="loginHref" class="header__menu__login btn btn-dark-blue">
            {{ trans("header.login") }}
          </a>
        </div>
        <mobile-menu-toggle class="header-menu-open"></mobile-menu-toggle>
      </div>
    </div>
    <mobile-menu></mobile-menu>
  </header>
  <header class="subheader-mobile">
    <div class="container">
      <div class="subheader-mobile__wrapper">
        <span v-if="activeLot?.lot_number" class="lot-number">
          {{ trans("live.common.main_info.lot_number", { number: activeLot?.lot_number }) }}&nbsp;
        </span>
        <span class="auction-title">{{ activeLot?.title || auction.title || "" }}</span>
      </div>
    </div>
  </header>
</template>

<script lang="ts" setup>
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { Menu as VMenu } from "floating-vue";
  import { trans } from "@/utils/trans";
  import { computed, watch } from "vue";
  import apiClient from "@/services/api";
  import { BaseIcon, WatchIcon } from "@/components/icons";
  import { LotStatuses } from "@/enum";
  import MobileMenuToggle from "@/components/menu-blocks/MobileMenuToggle.vue";
  import MobileMenu from "@/components/menu-blocks/MobileMenu.vue";
  import { ILanguage } from "@/models";

  const auctionStore = useAuctionStore();

  const routes = computed(() => auctionStore.routes);
  const auction = computed(() => auctionStore.auction);
  const lots = computed(() => auctionStore.lots);
  const liveLot = computed(() => lots.value.find((item) => item.status === LotStatuses.LIVE));
  const passedLots = computed(() =>
    lots.value.filter((item) => item.status === LotStatuses.LIVE || item.status === LotStatuses.ENDED)
  );

  const activeLot = computed(() =>
    auctionStore.activeLot?.id
      ? auctionStore.activeLot
      : auctionStore.fakeActiveLot?.id
        ? auctionStore.fakeActiveLot
        : liveLot.value
  );
  const auctionProgress = computed(() => parseInt(String((passedLots.value.length / lots.value.length) * 100)) || 0);
  const loginHref = computed(() => routes.value['login'] + '?redirect=' + location.href);

  const logout = () => {
    apiClient.post(routes.value["logout"]).then(() => {
      window.location.reload();
    });
  };

  const languages = computed(() => {
    return auctionStore.languageSettings.languages || [];
  });

  const switchLanguage = (language: ILanguage) => {
    apiClient.put(language.url).then((response) => {
      auctionStore.$patch((storeState) => {
        storeState.translations = response.data;
        storeState.languageSettings.language = language.abbr;
        storeState.languageSettings.htmlDirection = language.htmlDirection;
      });
    });
  };

  watch(
    () => auctionStore.languageSettings.language,
    (nv) => {
      const abbrArray = auctionStore.languageSettings?.languages.map((item) => item.abbr);
      const abbrs = abbrArray.join("|");
      let regexp = new RegExp(`^\\/(${abbrs})\\/`, "i");

      let matches = window.location.pathname.match(regexp);
      const abbrInPath = matches && matches?.length ? matches[1] : null;

      if (!nv) {
        if (abbrInPath && abbrArray?.includes(abbrInPath)) {
          let lang = auctionStore.languageSettings?.languages?.find((item) => item.abbr === abbrInPath);
          if (lang) {
            switchLanguage(lang);
          }
        }

        return;
      } else if (abbrInPath && nv == abbrInPath) {
        return;
      }

      const lang = auctionStore.languageSettings?.languages?.find((item) => item.abbr === nv);
      if (lang) {
        document.documentElement.setAttribute("lang", lang.abbr);
        document.documentElement.setAttribute("dir", lang.htmlDirection);

        let newPath = window.location.pathname;

        if (window.location.pathname.match(regexp)) {
          newPath = newPath.replace(regexp, `/${lang.abbr}/`);
        } else {
          newPath = `/${lang.abbr}${newPath}`;
        }
        newPath = newPath.replace(/^\/en\//, "/");

        window.history.replaceState("", "", newPath);
      }
    },
    { immediate: true }
  );
</script>
